import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, X } from 'lucide-react';
import { apiService } from '../../services/api';
import { useDebounce } from '../../hooks/useDebounce';

interface ResellerOption {
  value: string;
  label: string;
}

interface ResellerSearchProps {
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  includeMyClientsOption?: boolean;
  allLabel?: string;
  myClientsLabel?: string;
}

const ResellerSearch: React.FC<ResellerSearchProps> = ({
  value,
  onValueChange,
  placeholder = "Buscar revendedor...",
  className = '',
  includeMyClientsOption = true,
  allLabel = 'Todos os Revendedores',
  myClientsLabel = 'Apenas Meus Clientes'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [options, setOptions] = useState<ResellerOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const searchRef = useRef<HTMLDivElement>(null);

  // Opções padrão (usando useMemo para evitar recriação desnecessária)
  const defaultOptions: ResellerOption[] = React.useMemo(() => [
    { value: '', label: allLabel },
    ...(includeMyClientsOption ? [{ value: 'my_clients', label: myClientsLabel }] : [])
  ], [allLabel, includeMyClientsOption, myClientsLabel]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Buscar revendedores quando o termo de busca mudar
  useEffect(() => {
    const searchResellers = async () => {
      // Se o termo de busca for muito curto, mostrar apenas opções padrão
      if (debouncedSearchTerm.length < 2) {
        setOptions(defaultOptions);
        return;
      }

      try {
        setIsLoading(true);
        const response = await apiService.getResellers({ 
          search: debouncedSearchTerm, 
          per: 30 // Aumentar para 30 resultados
        });
        
        if (response.data && response.data.length > 0) {
          const resellersList = response.data
            .filter((r: any) => r != null && r.id !== undefined && r.id !== null && String(r.id).trim() !== '')
            .map((reseller: any) => ({
              value: String(reseller.id),
              label: `${reseller.username ?? '?'} (${reseller.id})`
            }));
          
          setOptions([...defaultOptions, ...resellersList]);
        } else {
          // Se não encontrou resultados, manter apenas opções padrão
          setOptions(defaultOptions);
        }
      } catch (error) {
        console.error('Erro ao buscar revendedores:', error);
        setOptions(defaultOptions);
      } finally {
        setIsLoading(false);
      }
    };

    // Buscar sempre que houver termo de busca (mesmo se dropdown não estiver aberto)
    searchResellers();
  }, [debouncedSearchTerm, defaultOptions]);

  // Atualizar label selecionado quando o valor mudar
  useEffect(() => {
    if (value) {
      const option = options.find(opt => opt.value === value);
      if (option) {
        setSelectedLabel(option.label);
      } else if (value !== '') {
        // Se temos um valor mas não está nas opções, buscar
        searchSpecificReseller(value);
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, options]);

  const searchSpecificReseller = async (resellerId: string) => {
    try {
      const response = await apiService.getReseller(resellerId);
      if (response) {
        setSelectedLabel(`${response.username} (${response.id})`);
      }
    } catch (error) {
      console.error('Erro ao buscar revendedor específico:', error);
    }
  };

  const handleOptionClick = (optionValue: string, optionLabel: string) => {
    onValueChange(optionValue);
    setSelectedLabel(optionLabel);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onValueChange('');
    setSelectedLabel('');
    setSearchTerm('');
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Quando abrir, se já tiver termo de busca, manter as opções
      // Caso contrário, mostrar opções padrão
      if (searchTerm.length < 2) {
        setOptions(defaultOptions);
      }
    }
  };

  // Abrir dropdown automaticamente quando começar a digitar
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    
    // Abrir dropdown automaticamente quando começar a digitar
    if (newValue.length >= 2 && !isOpen) {
      setIsOpen(true);
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:border-gray-400 transition-colors"
      >
        <span className="text-left flex-1 truncate text-sm">
          {selectedLabel || placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && value !== '' && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="p-1 hover:bg-gray-100 rounded transition-colors cursor-pointer"
              title="Limpar seleção"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleClear();
                }
              }}
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 transition-transform text-gray-500 ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b bg-gray-50">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Digite para buscar revendedor..."
                className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 text-center text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 mx-auto mb-2"></div>
                <span className="text-xs block">Buscando revendedores...</span>
              </div>
            ) : options.length === 0 || (options.length === defaultOptions.length && searchTerm.length >= 2) ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {searchTerm.length < 2 
                  ? 'Digite pelo menos 2 caracteres para buscar' 
                  : `Nenhum revendedor encontrado para "${searchTerm}"`
                }
              </div>
            ) : (
              <>
                {options.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleOptionClick(option.value, option.label)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm transition-colors ${
                      option.value === value ? 'bg-blue-50 text-blue-700 font-medium border-l-2 border-blue-500' : 'text-gray-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerSearch;
